import { useEffect, useMemo, useRef, useState } from 'react';
import Animated, {
    Easing,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';
import {
    BodyMediumRegular,
    ChevronLeftIcon,
    ChevronRightIcon,
    hitSlop,
    Icon,
    View,
    XStack,
} from 'smoothie';

import {
    type IncomeSplitOverviewData,
    type IncomeSplitOverviewHistory,
} from '../types/IncomeSplitOverviewTypes';

export const IncomeSplitMonthNavigator = ({
    data,
    currentMonthIndex,
    setCurrentMonthIndex,
}: {
    data: IncomeSplitOverviewData;
    currentMonthIndex: number;
    setCurrentMonthIndex: (arg0: (prev: number) => number) => void;
}) => {
    const incomeSplitMonth = useMemo(() => {
        return data.history[currentMonthIndex] as IncomeSplitOverviewHistory;
    }, [data.history, currentMonthIndex]);

    // Months before this index have no execution data, so there's nothing to navigate back to.
    const earliestMonthWithDataIndex = useMemo(() => {
        const index = data.history.findIndex(
            (month) => month.executions.length > 0,
        );
        return index === -1 ? 0 : index;
    }, [data.history]);
    const isAtEarliestMonthWithData =
        currentMonthIndex <= earliestMonthWithDataIndex;

    const monthLabelOpacity = useSharedValue(1);
    const [displayMonth, setDisplayMonth] = useState(incomeSplitMonth.month);
    const prevMonthRef = useRef(incomeSplitMonth.month);

    // Update text then start fade-in from JS so React commits the new label before we animate in
    const updateMonthAndFadeIn = (newMonth: string) => {
        setDisplayMonth(newMonth);
        prevMonthRef.current = newMonth;
        requestAnimationFrame(() => {
            monthLabelOpacity.value = withTiming(1, {
                duration: 300,
                easing: Easing.bezier(0.65, 0, 0.35, 1),
            });
        });
    };

    useEffect(() => {
        if (prevMonthRef.current === incomeSplitMonth.month) {
            if (displayMonth !== incomeSplitMonth.month) {
                setDisplayMonth(incomeSplitMonth.month);
            }
            return;
        }
        prevMonthRef.current = incomeSplitMonth.month;

        monthLabelOpacity.value = withTiming(
            0,
            {
                duration: 150,
                easing: Easing.bezier(0.65, 0, 0.35, 1),
            },
            () => {
                runOnJS(updateMonthAndFadeIn)(incomeSplitMonth.month);
            },
        );
    }, [incomeSplitMonth.month, displayMonth]);

    const animatedMonthLabelStyle = useAnimatedStyle(() => ({
        opacity: monthLabelOpacity.value,
    }));

    return (
        <XStack
            padding={16}
            gap={32}
            alignItems="center"
            justifyContent="space-between"
        >
            <View
                cursor={isAtEarliestMonthWithData ? 'auto' : 'pointer'}
                onPress={() => {
                    setCurrentMonthIndex((prev) =>
                        prev - 1 < earliestMonthWithDataIndex
                            ? earliestMonthWithDataIndex
                            : prev - 1,
                    );
                }}
                opacity={isAtEarliestMonthWithData ? 0 : 1}
                {...hitSlop(8)}
            >
                <Icon icon={ChevronLeftIcon} size={24} />
            </View>
            <Animated.View style={animatedMonthLabelStyle}>
                <BodyMediumRegular>
                    {new Date(displayMonth).toLocaleString('default', {
                        month: 'long',
                        timeZone: 'UTC',
                    })}
                </BodyMediumRegular>
            </Animated.View>
            <View
                cursor={
                    currentMonthIndex === data.history.length - 1
                        ? 'auto'
                        : 'pointer'
                }
                onPress={() => {
                    setCurrentMonthIndex((prev) =>
                        prev + 1 > data.history.length - 1
                            ? data.history.length - 1
                            : prev + 1,
                    );
                }}
                opacity={currentMonthIndex === data.history.length - 1 ? 0 : 1}
                {...hitSlop(8)}
            >
                <Icon icon={ChevronRightIcon} size={24} />
            </View>
        </XStack>
    );
};
